import React from "react";
import classNames from "classnames";
import { CrossNewIcon } from "components/icons/icons";
import IconButton from "components/iconButton/iconButton";
import {
  SimpleButton,
  buttonStyles,
} from "components/simpleButton/simpleButton";
import { TOP_BANNER_VARIANTS } from "lib/constants/topBanner";
import type { TopBannerContentProps } from "./types";
import type {
  ButtonAccent,
  SimpleButtonProps,
} from "components/simpleButton/simpleButton";

const BANNER_BUTTON_VARIANT_PRIMARY = "primary" as const;
const BANNER_BUTTON_VARIANT_SECONDARY = "secondary" as const;
const BANNER_BUTTON_SIZE_MD: SimpleButtonProps["size"] = "md";

type BannerButtonProps = {
  href?: string;
  label: string;
  accent: ButtonAccent;
  variant:
    | typeof BANNER_BUTTON_VARIANT_PRIMARY
    | typeof BANNER_BUTTON_VARIANT_SECONDARY;
  onClick?: () => void;
};

const BannerButton: React.FC<BannerButtonProps> = ({
  href,
  label,
  accent,
  variant,
  onClick,
}) => {
  if (href) {
    return (
      <a
        href={href}
        className={buttonStyles({
          variant,
          accent,
          size: BANNER_BUTTON_SIZE_MD,
        })}
        onClick={onClick}
      >
        {label}
      </a>
    );
  }
  return (
    <SimpleButton
      accent={accent}
      variant={variant}
      size={BANNER_BUTTON_SIZE_MD}
      onClick={onClick}
    >
      {label}
    </SimpleButton>
  );
};

const variantStyles = {
  [TOP_BANNER_VARIANTS.SIGN_IN]: "bg-sifted-20",
  [TOP_BANNER_VARIANTS.PRO]: "bg-pro-10",
};

export const TopBannerContent: React.FC<TopBannerContentProps> = ({
  variant,
  heading,
  subheading,
  primaryButtonLabel,
  primaryButtonHref,
  primaryButtonOnClick,
  secondaryButtonLabel,
  secondaryButtonHref,
  secondaryButtonOnClick,
  onClose = () => {},
  className,
  testId = "top-banner",
}) => {
  const buttonAccent: ButtonAccent =
    variant === TOP_BANNER_VARIANTS.PRO ? "pro" : "sifted";
  return (
    <div
      data-testid={testId}
      className={classNames(
        "relative w-full py-4",
        variantStyles[variant],
        className
      )}
    >
      <div className="mx-auto max-w-content-mobile px-4 md:max-w-content lg:max-w-container mdDesktop:max-w-container">
        <div className="flex flex-col gap-1 ">
          <div className="flex w-full items-start justify-between gap-2">
            <h2 className="font-recklessBold text-accent-24 leading-tight">
              {heading}
            </h2>
            <IconButton
              onClick={onClose}
              aria-label="Close banner"
              className="flex-shrink-0"
            >
              <CrossNewIcon />
            </IconButton>
          </div>

          <div className="w-full">
            <p className="mb-2 text-regular-16">{subheading}</p>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <BannerButton
                href={primaryButtonHref}
                label={primaryButtonLabel}
                accent={buttonAccent}
                variant={BANNER_BUTTON_VARIANT_PRIMARY}
                onClick={primaryButtonOnClick}
              />
              <BannerButton
                href={secondaryButtonHref}
                label={secondaryButtonLabel}
                accent={buttonAccent}
                variant={BANNER_BUTTON_VARIANT_SECONDARY}
                onClick={secondaryButtonOnClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
